import {
  globalizationDownload,
  useGetInternationalByIdsQuery,
} from "@/react/features/globalization/globalizationApi";
import React, { useRef, useState } from "react";
import {
  StyledPrinterIcon,
  WhiteCopyButton,
} from "@/react/components/styled/StyledIcons";
import { internationalHorizontal } from "@/react/features/globalization/columnDefinition";
import { MemoHorizontalTable } from "@/react/features/globalization/HorizontalTable";
import { useSelector } from "react-redux";
import { OpenInTapFAB } from "@/react/features/globalization/OpenInTapFAB";
import {
  displayToastMessage,
  useDismissableNotification,
} from "@/react/notifications";
import { TYPE } from "@8x8/oxygen-toaster";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactToPrint } from "react-to-print";
import {
  FilledDownloadIcon,
  LogoContainer,
  MarginLeftContainer,
  PageBreakTableWrapper,
  PrintNeverContainer,
  PrintOnlyContainer,
  StyledLogo,
} from "@/react/features/globalization/pages/styles";
import { GLOBALIZATION_INTERNATIONAL } from "@/react/routing/constants";
import { CustomButton } from "@/react/features/globalization/components/CustomButton";
import { ArrowLeftFullIcon } from "@8x8/oxygen-icon";
import { OperationsContainer } from "@/react/features/globalization/OperationsContainer";
import Button from "@8x8/oxygen-button";

const DEFAULT_NO_EDIT_RIGHTS = { rights: [] };

export function GlobalizationInternationalComparePage() {
  const [isCopied, setIsCopied] = useState(false);
  const isIframe = useSelector((state) => state.application.isIframe);
  const [searchParams] = useSearchParams();

  const ids = searchParams.getAll("id") || [];
  const { data: internationalList, isLoading } = useGetInternationalByIdsQuery({
    idList: ids,
  });

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const navigate = useNavigate();

  useDismissableNotification(isCopied ? "Copied link" : undefined, {
    type: TYPE.success,
  });

  const dateString = new Date(
    Date.now() + -1 * new Date().getTimezoneOffset() * 60000
  ).toISOString();

  const downloadExcel = (ids) => {
    globalizationDownload(
      "international/export?format=xlsx" + (ids ? "&ids=" + ids : "")
    ).then((isDownloaded) => {
      displayToastMessage(
        isDownloaded ? "Download success" : "Failed to download",
        {
          type: isDownloaded ? TYPE.info : TYPE.error,
        }
      );
    });
  };

  let printableRef = useRef();

  return (
    <OpenInTapFAB>
      <PrintOnlyContainer id="compare_table_export" ref={printableRef}>
        <LogoContainer>
          <StyledLogo alt="Logo" id="compare_page_logo" />
          <span>Generated at {dateString}</span>
        </LogoContainer>
        <MarginLeftContainer>
          <>
            {internationalList &&
              internationalList.length > 0 &&
              internationalList.map((international, index) => (
                <PageBreakTableWrapper index={index} key={international.id}>
                  <MemoHorizontalTable
                    key={international.id}
                    title={international.country}
                    isLoading={isLoading}
                    data={[international]}
                    columns={internationalHorizontal}
                    meta={DEFAULT_NO_EDIT_RIGHTS}
                  />
                </PageBreakTableWrapper>
              ))}
          </>
        </MarginLeftContainer>
      </PrintOnlyContainer>
      <PrintNeverContainer id="compare_table">
        <OperationsContainer isStacked={isIframe}>
          <CustomButton
            onClick={() => {
              navigate(GLOBALIZATION_INTERNATIONAL);
            }}
          >
            <ArrowLeftFullIcon size={20} /> Back
          </CustomButton>
          <Button
            variant="success"
            size="small"
            onClick={handleCopyLink}
            id="copy-link"
          >
            <WhiteCopyButton size={20} /> Copy link
          </Button>
          <Button
            variant="success"
            size="small"
            onClick={() => downloadExcel(ids)}
          >
            <FilledDownloadIcon /> Export selected
          </Button>
          <ReactToPrint
            trigger={() => (
              <Button id="print" variant="success" size="small">
                <StyledPrinterIcon size={20} /> Print
              </Button>
            )}
            content={() => printableRef.current}
          />
        </OperationsContainer>
        <MemoHorizontalTable
          isLoading={isLoading}
          title={ids.length > 1 ? "Global Compare" : "View"}
          data={internationalList}
          columns={internationalHorizontal}
          meta={DEFAULT_NO_EDIT_RIGHTS}
        />
      </PrintNeverContainer>
    </OpenInTapFAB>
  );
}
